import { Dialog } from "@components";
import React, { useState, FC } from "react";
import { useTranslation } from "react-i18next";
import { PublicProduct } from "../../../../api/publicOrder";

interface PublicSavedProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    selectedProducts: { product: PublicProduct; quantity: number }[],
  ) => void;
  products: PublicProduct[];
  isLoading?: boolean;
  config?: any;
}

const PublicSavedProductsForm: FC<
  Omit<PublicSavedProductsModalProps, "isOpen" | "onClose">
> = ({ onConfirm, products, isLoading = false }) => {
  const { t } = useTranslation(["order_form"]);
  const [selectedProducts, setSelectedProducts] = useState<{
    [key: string]: number;
  }>({});

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    const productsToAdd = Object.entries(selectedProducts)
      .filter(([_, quantity]) => quantity > 0)
      .map(([productId, quantity]) => ({
        product: products.find((p) => p.id === productId)!,
        quantity,
      }));

    onConfirm(productsToAdd);
  };

  const handleQuantityChange = (productId: string, quantity: number) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [productId]: Math.max(0, quantity),
    }));
  };

  return (
    <form id="public-saved-products-form" onSubmit={handleConfirm}>
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t("public_form.common.no_products_available")}
          </div>
        ) : (
          products.map((product) => (
            <div
              key={product.id}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <div className="flex-1">
                <h3 className="font-medium text-text">
                  {product.descriptionShort}
                </h3>
                <p className="text-sm text-muted">
                  {product.description}
                </p>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-muted">
                  <div>
                    <span className="font-medium">
                      {t("public_form.common.description")}:
                    </span>
                    <br />
                    {product.length}x{product.width}x{product.height}{" "}
                    {t("public_form.common.cm")}
                  </div>
                  <div>
                    <span className="font-medium">
                      {t("public_form.common.weight")}:
                    </span>
                    <br />
                    {product.weight} {t("public_form.common.kg")}
                  </div>
                  <div>
                    <span className="font-medium">
                      {t("public_form.common.price")}:
                    </span>
                    <br />
                    {product.price} {t("public_form.common.currency")}
                  </div>
                </div>
              </div>
              <div className="ml-4">
                <input
                  type="number"
                  min="0"
                  value={selectedProducts[product.id] || 0}
                  onChange={(e) =>
                    handleQuantityChange(
                      product.id,
                      parseInt(e.target.value) || 0,
                    )
                  }
                  className="w-20 px-2 py-1 border border-border-strong rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          ))
        )}
      </div>
    </form>
  );
};

const PublicSavedProductsModal: React.FC<PublicSavedProductsModalProps> = (
  props,
) => {
  const { t } = useTranslation(["order_form", "common"]);

  return (
    <Dialog
      title={t("public_form.form.cargo.select_from_list")}
      show={props.isOpen}
      toggleModal={props.onClose}
      buttonConfirmText={t("public_form.common.add_selected_products")}
      buttonCloseText={t("common:close")}
      confirmButtonColor="primary"
      confirmButton={() => {
        document
          .getElementById("public-saved-products-form")
          ?.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true }),
          );
      }}
      content={<PublicSavedProductsForm {...props} />}
      size="xl"
    />
  );
};

export default PublicSavedProductsModal;
